import React, { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeftIcon, ChevronRightIcon, HeartIcon as EmptyHeartIcon } from "@heroicons/react/24/outline";
import { HeartIcon as FullHeartIcon } from "@heroicons/react/24/solid";

import AuthContext from "../util/AuthContext";
import { useNotification } from "../util/NotificationContext";
import { useSearch } from "../util/SearchContext";

export default function RecipeGrid({ recipes, setRecipes, showFavorite }) {
  const { login, user, favorites, addFavorite, removeFavorite } = useContext(AuthContext);
  const location = useLocation();
  const { addNotification } = useNotification();

  const { setSearchQuery } = useSearch();

  // Updated handler functions that compute a combined images array.
  const handlePrevImage = (recipe, imagesArray) => {
    if (!imagesArray.length) return;
    if (typeof recipe.imageIndex !== "number") recipe.imageIndex = 0;
    recipe.imageIndex = (recipe.imageIndex - 1 + imagesArray.length) % imagesArray.length;
    setRecipes([...recipes]);
  };

  const handleNextImage = (recipe, imagesArray) => {
    if (!imagesArray.length) return;
    if (typeof recipe.imageIndex !== "number") recipe.imageIndex = 0;
    recipe.imageIndex = (recipe.imageIndex + 1) % imagesArray.length;
    setRecipes([...recipes]);
  };

  const handleFavorite = (recipeId) => {
    if (!user) {
      login();
      return;
    }
    if (favorites.has(recipeId)) {
      removeFavorite(recipeId);
      addNotification("Recipe unsaved", "This recipe has been removed from your favorites.", "success");
    } else {
      addFavorite(recipeId);
      addNotification("Recipe saved", "You can find it later in My Recipes.", "success");
    }
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-2 sm:gap-x-3 gap-y-4">
      {recipes.map((recipe) => {
        const isFavorited = favorites.has(recipe.id);

        const imagesArray =
          recipe.images && recipe.images.length > 0
            ? recipe.images
            : recipe.originalRecipe?.defaultImageUrl
              ? [recipe.originalRecipe.defaultImageUrl]
              : ["/placeholder.jpg"];

        const currentPage = location.pathname + location.search;

        return (
          <Link
            key={recipe.id}
            to={`/recipe/${recipe.id}`}
            state={{ from: currentPage }}
            onClick={() => setSearchQuery("")}
            className="bg-white border border-gray-300 rounded-lg overflow-hidden cursor-pointer"
          >
            <div className="relative group">
              <img
                src={imagesArray[recipe.imageIndex || 0] || "/placeholder.jpg"}
                alt={recipe.title}
                className="w-full h-40 sm:h-48 md:h-64 object-cover rounded-t-lg"
              />
              {imagesArray.length > 1 && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePrevImage(recipe, imagesArray);
                  }}
                  className="absolute left-1 sm:left-2 top-1/2 transform -translate-y-1/2 p-2 bg-white rounded-full shadow-md hidden group-hover:block"
                >
                  <ChevronLeftIcon className="w-3 sm:w-5 h-3 sm:h-5 text-gray-600" />
                </button>
              )}
              {imagesArray.length > 1 && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNextImage(recipe, imagesArray);
                  }}
                  className="absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 p-2 bg-white rounded-full shadow-md hidden group-hover:block"
                >
                  <ChevronRightIcon className="w-3 sm:w-5 h-3 sm:h-5 text-gray-600" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFavorite(recipe.id);
                }}
                className="absolute top-2 right-2"
              >
                {showFavorite &&
                  (isFavorited ? (
                    <FullHeartIcon
                      className="w-6 sm:w-7 h-6 sm:h-7 text-green-600 hover:scale-110 transition duration-200"
                      style={{
                        stroke: "white",
                        strokeWidth: "1px",
                      }}
                    />
                  ) : (
                    <EmptyHeartIcon
                      className="w-6 sm:w-7 h-6 sm:h-7 text-gray-600 hover:scale-110 transition duration-200"
                      style={{
                        fill: "rgba(0, 0, 0, 0.7)",
                        stroke: "white",
                        strokeWidth: "1px",
                      }}
                    />
                  ))}
              </button>
            </div>
            <div className="pt-2 pb-2 px-2">
              <h4 className="text-md font-semibold truncate">{recipe.title}</h4>
              <p className="text-sm text-gray-500 truncate">{recipe.cookTime}</p>
              <p className="text-sm text-gray-500 italic mt-2 truncate">
                {recipe.originalRecipe?.faviconUrl && (
                  <img src={recipe.originalRecipe?.faviconUrl} alt="" className="w-4 h-4 inline mr-1" />
                )}
                {recipe.originalRecipe?.baseDomain || "unknown"}
              </p>
            </div>
          </Link>
        );
      })}
    </div>
  );
}
